import { useEffect, useMemo, useState } from 'react'
import { Film } from 'lucide-react'
import { useQueryClient } from '@tanstack/react-query'
import {
  ApiError,
  PlannedMove,
  SwitchList,
  SwitchListPreview,
  SwitchListPrintSheet,
  buildSwitchListPrintViewModel,
  type SwitchListPrintViewModel,
  TrackCar,
  useActiveCrewsQuery,
  useAvailableLocomotivesQuery,
  useDispatchWorkOrderMutation,
  useGenerateSwitchListMutation,
  useRoutableTracksQuery,
  useTrackCarsQuery,
  useTrackSpotsQuery,
} from '@nyos/shared'
import { useYard } from '../../../contexts/YardContext'
import ScreenShell from '../../../components/ScreenShell'
import ScenarioControls from '../../../components/ScenarioControls'
import { BuilderMove, SortableMoveList } from '../components/SortableMoveList'
import TrackIndex from '../components/TrackIndex'
import TrackStrip, { PullEnd } from '../components/TrackStrip'
import DestinationRail from '../components/DestinationRail'
import SwitchListPreviewMapModal from '../../../components/SwitchListPreviewMapModal'

/**
 * Switch Job Builder — EDI-independent move authoring (Plan 24 §5.2/§5.4).
 *
 * Ops-console layout (three-zone cockpit):
 *   header  — title, snapshot-age chip, draft state, ScenarioControls
 *             (SWITCH_LIST scenarios seed rail cars onto tracks — the demo
 *             yard loader; cleanup restores a blank yard)
 *   work    — TrackIndex | TrackStrip + DestinationRail | move stack
 *   footer  — Preview / Validate & Dispatch / Print
 *
 * Mechanics preserved from §5.2/§5.4: reachable-end anchoring (loco glyph
 * pins the pull end; click flips it), projected-state working copy,
 * @dnd-kit pass ordering, idempotency key per dispatch attempt,
 * snapshot-age + revalidation, autosave draft, yard-language errors.
 */

// Draft key is facility-scoped — a draft written under facility A must
// never restore under facility B (foreign track/car ids → stale plan).
const DRAFT_KEY_PREFIX = 'nyos_switch_job_draft_v1'

interface DraftMove {
  sourceTrackId: string
  targetTrackId: string
  targetSpotId?: string
  targetDoorNumber?: string
  carIds: string[]
}

interface BuilderDraft {
  locomotiveId: string
  locomotiveTrackId?: string
  crewId: string
  moves: DraftMove[]
}

export default function SwitchJobBuilderPage() {
  const queryClient = useQueryClient()
  const { activeFacilityId } = useYard()

  // --- server state (React Query) ---
  const tracksQuery = useRoutableTracksQuery(activeFacilityId)
  const locomotivesQuery = useAvailableLocomotivesQuery(activeFacilityId)
  const crewsQuery = useActiveCrewsQuery()
  const generateMutation = useGenerateSwitchListMutation(activeFacilityId)
  const dispatchMutation = useDispatchWorkOrderMutation(activeFacilityId)

  const tracks = useMemo(() => tracksQuery.data ?? [], [tracksQuery.data])
  const locomotives = useMemo(() => locomotivesQuery.data ?? [], [locomotivesQuery.data])
  const crews = useMemo(() => crewsQuery.data ?? [], [crewsQuery.data])

  // --- client UI state ---
  const [sourceTrackId, setSourceTrackId] = useState('')
  const [pullEnd, setPullEnd] = useState<PullEnd>('A')
  const [cutAnchorIdx, setCutAnchorIdx] = useState<number | null>(null)
  const [targetTrackId, setTargetTrackId] = useState('')
  const [targetDoorNumber, setTargetDoorNumber] = useState('')
  const [targetSpotId, setTargetSpotId] = useState('')
  const [locomotiveId, setLocomotiveId] = useState('')
  const [locomotiveTrackId, setLocomotiveTrackId] = useState('')
  const [crewId, setCrewId] = useState('')
  const [moves, setMoves] = useState<BuilderMove[]>([])
  const [preview, setPreview] = useState<SwitchList | null>(null)
  const [animateOpen, setAnimateOpen] = useState(false)
  const [printModel, setPrintModel] = useState<SwitchListPrintViewModel | null>(null)
  const [errors, setErrors] = useState<string[]>([])
  const [idempotencyKey, setIdempotencyKey] = useState('')
  const [dispatched, setDispatched] = useState(false)
  const [draftPrompt, setDraftPrompt] = useState<BuilderDraft | null>(null)
  const [staleNotice, setStaleNotice] = useState<string | null>(null)

  const draftKey = `${DRAFT_KEY_PREFIX}:${activeFacilityId}`

  const trackCarsQuery = useTrackCarsQuery(activeFacilityId, sourceTrackId || undefined)
  const spotsQuery = useTrackSpotsQuery(targetTrackId || undefined)

  const sourceCars = useMemo(() => trackCarsQuery.data?.cars ?? [], [trackCarsQuery.data])
  const carsFetchedAt = trackCarsQuery.dataUpdatedAt ? new Date(trackCarsQuery.dataUpdatedAt) : null
  const targetSpots = useMemo(
    () => (spotsQuery.data ?? []).filter((s) => s.isActive !== false),
    [spotsQuery.data]
  )

  /** Projected working copy — source track minus cars pulled by accepted moves. */
  const pulledIds = useMemo(
    () =>
      new Set(
        moves.filter((m) => m.sourceTrackId === sourceTrackId).flatMap((m) => m.carIds)
      ),
    [moves, sourceTrackId]
  )
  const projectedCars = useMemo(
    () => sourceCars.filter((c) => !pulledIds.has(c.railCarId)),
    [sourceCars, pulledIds]
  )

  /** Selected cut: contiguous block from the pull end through the anchor car. */
  const selectedCut = useMemo(() => {
    if (cutAnchorIdx === null || cutAnchorIdx < 0 || cutAnchorIdx >= projectedCars.length) {
      return [] as TrackCar[]
    }
    return pullEnd === 'A'
      ? projectedCars.slice(0, cutAnchorIdx + 1)
      : projectedCars.slice(cutAnchorIdx)
  }, [projectedCars, pullEnd, cutAnchorIdx])

  const pendingCutFeet = useMemo(
    () => selectedCut.reduce((sum, c) => sum + (c.lengthFeet ?? 50), 0),
    [selectedCut]
  )

  // Offer draft restore on mount (page entry = "on open" parity).
  useEffect(() => {
    const raw = localStorage.getItem(draftKey)
    if (!raw) {
      return
    }
    try {
      const draft = JSON.parse(raw) as BuilderDraft
      if (draft.moves?.length) {
        setDraftPrompt(draft)
      }
    } catch {
      localStorage.removeItem(draftKey)
    }
  }, [draftKey])

  // Scope switch: moves carry foreign track/car ids from the previous
  // facility — reset the whole builder draft so nothing leaks across.
  useEffect(() => {
    setSourceTrackId('')
    setPullEnd('A')
    setCutAnchorIdx(null)
    setTargetTrackId('')
    setTargetDoorNumber('')
    setTargetSpotId('')
    setLocomotiveId('')
    setLocomotiveTrackId('')
    setCrewId('')
    setMoves([])
    setPreview(null)
    setPrintModel(null)
    setErrors([])
    setIdempotencyKey('')
    setDispatched(false)
    setDraftPrompt(null)
    setStaleNotice(null)
    setAnimateOpen(false)
  }, [activeFacilityId])

  // Reset selection when the source track changes; keep only moves that
  // belong to the new source.
  useEffect(() => {
    setCutAnchorIdx(null)
    setStaleNotice(null)
    setMoves((prev) =>
      prev.filter((m) => !sourceTrackId || m.sourceTrackId === sourceTrackId)
    )
  }, [sourceTrackId])

  // Reset spot/door hint when the destination changes — stale values must
  // not leak across tracks.
  useEffect(() => {
    setTargetSpotId('')
    setTargetDoorNumber('')
  }, [targetTrackId])

  // Autosave draft whenever the job spec changes.
  useEffect(() => {
    if (moves.length === 0) {
      return
    }
    const draft: BuilderDraft = {
      locomotiveId,
      locomotiveTrackId: locomotiveTrackId || undefined,
      crewId,
      moves: moves.map((m) => ({
        sourceTrackId: m.sourceTrackId ?? '',
        targetTrackId: m.targetTrackId,
        targetSpotId: m.targetSpotId,
        targetDoorNumber: m.targetDoorNumber,
        carIds: m.carIds,
      })),
    }
    localStorage.setItem(draftKey, JSON.stringify(draft))
  }, [moves, locomotiveId, locomotiveTrackId, crewId])

  const restoreDraft = () => {
    if (!draftPrompt) {
      return
    }
    setLocomotiveId(draftPrompt.locomotiveId)
    setLocomotiveTrackId(draftPrompt.locomotiveTrackId ?? '')
    setCrewId(draftPrompt.crewId)
    setMoves(
      draftPrompt.moves.map((m, i) => ({
        clientId: crypto.randomUUID(),
        moveKind: 'MOVE',
        sourceTrackId: m.sourceTrackId,
        targetTrackId: m.targetTrackId,
        targetSpotId: m.targetSpotId,
        targetDoorNumber: m.targetDoorNumber,
        carIds: m.carIds,
        priority: i + 1,
      }))
    )
    if (draftPrompt.moves[0]?.sourceTrackId) {
      setSourceTrackId(draftPrompt.moves[0].sourceTrackId)
    }
    setDraftPrompt(null)
  }

  const discardDraft = () => {
    localStorage.removeItem(draftKey)
    setDraftPrompt(null)
  }

  const selectCar = (idx: number) => {
    setCutAnchorIdx((prev) => (prev === idx ? null : idx))
  }

  const flipPullEnd = () => {
    setPullEnd((prev) => (prev === 'A' ? 'B' : 'A'))
    setCutAnchorIdx(null)
  }

  const selectSpot = (spotId: string) => {
    setTargetSpotId(spotId)
    const spot = targetSpots.find((s) => s.id === spotId)
    setTargetDoorNumber(spot?.doorNumber ?? '')
  }

  /** After scenario setup/cleanup the physical yard changed — refetch cars + track stats + crewing pickers. */
  const refreshYardData = () => {
    queryClient.invalidateQueries({ queryKey: ['track-cars'] })
    queryClient.invalidateQueries({ queryKey: ['routable-tracks'] })
    queryClient.invalidateQueries({ queryKey: ['locomotives'] })
    queryClient.invalidateQueries({ queryKey: ['crews'] })
    setCutAnchorIdx(null)
  }

  const invalidatePreview = () => {
    setPreview(null)
    setErrors([])
    generateMutation.reset()
  }

  // F-SWL-4 revalidation: accepted moves and restored drafts carry carIds
  // captured at accept time. When live inventory for the source track
  // lands or changes (first fetch, refreshYardData, refetch), prune car
  // ids that are no longer standing there — a move left empty is dropped.
  // React Query's track-cars data is the single source of truth; the
  // effect intentionally skips unfetched/errored states so a failed
  // refetch never nukes a valid job.
  const carsReady =
    !!sourceTrackId && trackCarsQuery.isFetched && !trackCarsQuery.isError
  useEffect(() => {
    if (!carsReady || moves.length === 0) {
      return
    }
    const standing = new Set(sourceCars.map((c) => c.railCarId))
    let droppedCars = 0
    let droppedMoves = 0
    const next = moves
      .map((m) => {
        const kept = m.carIds.filter((id) => standing.has(id))
        if (kept.length === m.carIds.length) {
          return m
        }
        droppedCars += m.carIds.length - kept.length
        return { ...m, carIds: kept }
      })
      .filter((m) => {
        if (m.carIds.length === 0) {
          droppedMoves++
          return false
        }
        return true
      })
      .map((m, i) => ({ ...m, priority: i + 1 }))
    if (droppedCars > 0) {
      setMoves(next)
      setStaleNotice(
        `Yard changed — removed ${droppedCars} car(s) no longer on ${sourceTrackName || 'the source track'}` +
          (droppedMoves > 0 ? `; dropped ${droppedMoves} empty move(s)` : '') +
          '. Re-run preview to revalidate.'
      )
      invalidatePreview()
    }
    // moves/sourceCars identity: pruning writes back through setMoves, so
    // including `moves` in deps would loop only if pruning produced a
    // different array every run — it can't (droppedCars > 0 implies the
    // second pass is a no-op). Preview invalidation is side-effect-only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [carsReady, sourceCars])

  const acceptMove = () => {
    if (!selectedCut.length || !targetTrackId) {
      return
    }
    setMoves((prev) => [
      ...prev,
      {
        clientId: crypto.randomUUID(),
        moveKind: 'MOVE',
        sourceTrackId,
        targetTrackId,
        targetSpotId: targetSpotId || undefined,
        targetDoorNumber: targetDoorNumber || undefined,
        carIds: selectedCut.map((c) => c.railCarId),
        priority: prev.length + 1,
      },
    ])
    setCutAnchorIdx(null)
    setTargetSpotId('')
    setTargetDoorNumber('')
    invalidatePreview()
  }

  const removeMove = (index: number) => {
    setMoves((prev) =>
      prev.filter((_, i) => i !== index).map((m, i) => ({ ...m, priority: i + 1 }))
    )
    setCutAnchorIdx(null)
    invalidatePreview()
  }

  const reorderMove = (from: number, to: number) => {
    setMoves((prev) => {
      const next = [...prev]
      const [moved] = next.splice(from, 1)
      next.splice(to, 0, moved)
      return next.map((m, i) => ({ ...m, priority: i + 1 }))
    })
    invalidatePreview()
  }

  const generatePreview = async (): Promise<SwitchList | null> => {
    setErrors([])
    setPreview(null)
    try {
      const list = await generateMutation.mutateAsync({
        locomotiveId,
        currentTrackId: locomotiveTrackId || undefined,
        plannedMoves: moves as PlannedMove[],
        // AUD-1 parity: the work-order dispatch path treats every builder
        // job as a coupling work order (loco couples to the cut). Declare
        // the same operation here or the preview's micro-step flags
        // (couplingDestination) diverge from the dispatched list.
        couplingWorkOrder: true,
      })
      setPreview(list)
      return list
    } catch (e) {
      if (e instanceof ApiError) {
        setErrors(e.messages)
      } else {
        setErrors([e instanceof Error ? e.message : 'Switch list generation failed'])
      }
      return null
    }
  }

  // Dispatch = revalidate current yard state (snapshot may have drifted)
  // then submit with one idempotency key per attempt.
  const dispatch = async () => {
    if (dispatchMutation.isPending) {
      return
    }
    setErrors([])
    const fresh = await generatePreview()
    if (!fresh) {
      return
    }
    const key = idempotencyKey || crypto.randomUUID()
    setIdempotencyKey(key)
    try {
      await dispatchMutation.mutateAsync({
        sourceTrackId: moves[0].sourceTrackId ?? '',
        targetTrackId: moves[moves.length - 1].targetTrackId,
        crewId,
        locomotiveId,
        // AUD-1 divergence fix (Option A): dispatch replays the same
        // generation phase this page's preview ran, so the order the
        // yardmaster approved is the order the crew receives.
        switchListMode: 'BUILDER',
        plannedCuts: moves.map((m, i) => ({
          passNumber: m.priority ?? i + 1,
          sourceTrackId: m.sourceTrackId ?? '',
          trackId: m.targetTrackId,
          locomotiveId,
          carIds: m.carIds,
        })),
      })
      localStorage.removeItem(draftKey)
      setDispatched(true)
    } catch (e) {
      setIdempotencyKey('')
      setErrors([e instanceof Error ? e.message : 'Dispatch failed'])
    }
  }

  // Print via the shared template (Plan 26 1c-i) — the same view-model that
  // drives the on-screen preview drives the print sheet.
  const printSwitchList = () => {
    if (!preview) {
      return
    }
    const loco = locomotives.find((l) => l.id === locomotiveId)
    const crew = crews.find((c) => c.crewId === crewId)
    setPrintModel(
      buildSwitchListPrintViewModel(preview, {
        locomotiveMark: loco?.reportingMark ?? locomotiveId,
        crewName: crew?.crewName,
        workOrderId: preview.id,
        origin: window.location.origin,
      })
    )
  }

  const resetAll = () => {
    setSourceTrackId('')
    setCutAnchorIdx(null)
    setMoves([])
    setTargetTrackId('')
    setTargetDoorNumber('')
    setTargetSpotId('')
    setPreview(null)
    setErrors([])
    setDispatched(false)
    setIdempotencyKey('')
  }

  const trackName = (trackId: string | undefined) =>
    tracks.find((t) => t.id === trackId)?.name ?? trackId ?? ''

  const sourceTrackName = trackName(sourceTrackId)
  const targetTrackName = trackName(targetTrackId)
  const canPreview = moves.length > 0 && !!locomotiveId && !generateMutation.isPending
  // F-SWL-3: a BLOCKED pass gates dispatch — the plan asks the loco to do
  // something it physically cannot. WARN-only lists stay dispatchable.
  const blockedPasses = (preview?.items ?? []).filter((i) => i.status === 'BLOCKED')
  const canDispatch =
    moves.length > 0 &&
    !!crewId &&
    !!locomotiveId &&
    preview != null &&
    blockedPasses.length === 0 &&
    !dispatchMutation.isPending &&
    !dispatched

  const selectCls =
    'w-full px-2.5 py-1.5 border border-border rounded-md bg-background text-foreground text-sm'

  return (
    <ScreenShell
      title="Switch List Builder"
      subtitle="Plan cuts against live track inventory — physics-checked before it reaches a crew"
      context={
        <div className="flex items-center gap-2">
          {carsFetchedAt && sourceTrackId && (
            <span className="px-2 py-0.5 rounded-md border border-border text-xs font-mono text-muted-foreground">
              yard state {carsFetchedAt.toLocaleTimeString()}
            </span>
          )}
          {moves.length > 0 && !dispatched && (
            <span className="px-2 py-0.5 rounded-md border border-amber-500/50 text-xs font-mono text-amber-500">
              DRAFT · autosaved
            </span>
          )}
          {dispatched && (
            <span className="px-2 py-0.5 rounded-md border border-switch-normal/60 text-xs font-mono text-switch-normal">
              DISPATCHED
            </span>
          )}
        </div>
      }
      actions={
        <div className="flex items-center gap-3">
          <ScenarioControls
            workflowType="SWITCH_LIST"
            onSetupComplete={refreshYardData}
            onCleanupComplete={refreshYardData}
          />
          {moves.length > 0 && (
            <button
              onClick={resetAll}
              className="text-sm text-muted-foreground hover:text-foreground underline"
            >
              Clear job
            </button>
          )}
        </div>
      }
      footer={
        <>
          {/* T4 (plan-30): the Add-move action lives in the footer — zone-2's
              overflow-hidden clipped it at 641px height. Always mounted with
              a stable min-w so the footer doesn't jitter as the label and
              enabled state change. */}
          <button
            onClick={acceptMove}
            disabled={!(selectedCut.length > 0 && targetTrackId)}
            className="min-w-[18rem] px-4 py-1.5 rounded-md bg-primary text-primary-foreground text-sm font-semibold disabled:opacity-40 pointer-coarse:min-h-11"
          >
            {selectedCut.length > 0 && targetTrackId
              ? <>
                  Add move — {selectedCut.length} car(s) → {targetTrackName}
                  {targetSpotId && targetSpots.find((s) => s.id === targetSpotId)
                    ? ` @ ${targetSpots.find((s) => s.id === targetSpotId)?.spotIdentifier ?? ''}`
                    : targetDoorNumber
                      ? ` door ${targetDoorNumber}`
                      : ''}
                </>
              : 'Add move'}
          </button>
          <button
            onClick={() => generatePreview()}
            disabled={!canPreview}
            className="px-4 py-1.5 rounded-md border border-border text-sm font-medium disabled:opacity-40"
          >
            {generateMutation.isPending
              ? 'Generating…'
              : preview
                ? 'Re-validate Preview'
                : 'Preview Switch List'}
          </button>
          {preview && (
            <button
              onClick={printSwitchList}
              className="px-4 py-1.5 rounded-md border border-border text-sm"
            >
              Print
            </button>
          )}
          <button
            onClick={dispatch}
            disabled={!canDispatch}
            className="px-4 py-1.5 rounded-md bg-primary text-primary-foreground text-sm font-semibold disabled:opacity-40"
            title={
              moves.length === 0
                ? 'Add at least one move'
                : !locomotiveId
                  ? 'Select a locomotive'
                  : blockedPasses.length > 0
                    ? `Resolve ${blockedPasses.length} blocked pass(es) first — see pass reason(s) below`
                    : !crewId
                      ? 'Select a crew'
                      : preview == null
                        ? 'Run Preview Switch List first'
                        : 'Revalidates against current yard state, then dispatches'
            }
          >
            {dispatchMutation.isPending ? 'Dispatching…' : 'Validate & Dispatch Work Order'}
          </button>
        </>
      }
    >
      <div className="h-full flex flex-row gap-4 p-4 overflow-hidden min-h-0">
        {/* Zone 1 — track index */}
        <div className="w-56 shrink-0 min-h-0">
          <TrackIndex tracks={tracks} selectedId={sourceTrackId} onSelect={setSourceTrackId} />
        </div>

        {/* Zone 2 — source strip + destination rail + preview. Scrolls as
            one column at tight heights so the destination select is never
            clipped below the fold (tablet). */}
        <div className="flex-1 min-w-0 flex flex-col gap-3 overflow-y-auto pr-1">
          {draftPrompt && (
            <div className="shrink-0 px-3 py-2 rounded-lg border border-amber-500/40 bg-amber-500/10 flex items-center justify-between text-sm">
              <span>
                A draft job with {draftPrompt.moves.length} move(s) was autosaved.
              </span>
              <div className="flex gap-2">
                <button
                  onClick={restoreDraft}
                  className="px-3 py-1 rounded-md bg-primary text-primary-foreground font-medium"
                >
                  Restore
                </button>
                <button
                  onClick={discardDraft}
                  className="px-3 py-1 rounded-md border border-border"
                >
                  Discard
                </button>
              </div>
            </div>
          )}
          {staleNotice && (
            <div className="shrink-0 px-3 py-2 rounded-lg border border-amber-500/50 bg-amber-500/10 text-sm text-amber-500 flex items-center justify-between">
              <span>{staleNotice}</span>
              <button onClick={() => setStaleNotice(null)} className="underline text-xs">
                Dismiss
              </button>
            </div>
          )}
          {dispatched && (
            <div className="shrink-0 px-3 py-2 rounded-lg border border-switch-normal/50 bg-switch-normal/10 text-sm font-medium flex items-center justify-between">
              <span>Work order dispatched — crew notified via the mobile queue.</span>
              <button onClick={resetAll} className="underline text-muted-foreground">
                Build another job
              </button>
            </div>
          )}

          <TrackStrip
            trackName={sourceTrackName}
            cars={projectedCars}
            pullEnd={pullEnd}
            onFlipPullEnd={flipPullEnd}
            cutAnchorIdx={cutAnchorIdx}
            onSelectCar={selectCar}
            loading={!!sourceTrackId && trackCarsQuery.isPending}
            fetchedAt={carsFetchedAt}
          />

          <DestinationRail
            tracks={tracks}
            excludedTrackId={sourceTrackId}
            targetTrackId={targetTrackId}
            onSelectTrack={setTargetTrackId}
            spots={targetSpots}
            targetSpotId={targetSpotId}
            onSelectSpot={selectSpot}
            doorNumber={targetDoorNumber}
            onDoorChange={setTargetDoorNumber}
            pendingCutFeet={pendingCutFeet}
            enabled={selectedCut.length > 0}
          />

          {errors.length > 0 && (
            <div className="shrink-0 p-3 rounded-lg border border-destructive/50 bg-destructive/10 text-sm">
              <div className="font-semibold mb-1">Move rejected</div>
              <ul className="list-disc pl-5 space-y-0.5">
                {errors.map((e, i) => (
                  <li key={i}>{e}</li>
                ))}
              </ul>
            </div>
          )}

          {(generateMutation.isPending || preview) && (
            // Same floor as the move stack — the shrink-0 items above
            // otherwise collapse this to a strip and the Animate button
            // straddles the box edge. Zone scrolls for the rest.
            <section className="flex-1 min-h-[14rem] rounded-lg border border-subtle bg-card p-3 flex flex-col">
              <div className="shrink-0 flex items-center justify-between mb-2">
                <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Switch List Preview
                </span>
                <button
                  type="button"
                  onClick={() => setAnimateOpen(true)}
                  disabled={!preview || generateMutation.isPending || !activeFacilityId}
                  className="px-3 py-1.5 rounded bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5 transition-colors"
                  title={activeFacilityId ? 'Animate this switch list on the yard map' : 'Facility scope unavailable — cannot animate'}
                >
                  <Film size={13} /> Animate
                </button>
              </div>
              <div className="flex-1 min-h-0 overflow-y-auto">
                <SwitchListPreview switchList={preview} loading={generateMutation.isPending} />
              </div>
            </section>
          )}
        </div>

        {/* Zone 3 — move stack + crewing. Whole panel scrolls at tight
            heights; the move stack also scrolls internally for long lists. */}
        <div className="w-64 shrink-0 flex flex-col gap-3 min-h-0 overflow-y-auto">
          {/* min-h floor: flex-1 alone collapses to a sleek strip when
              Crewing's auto-sized content claims the column — the stack
              stays a readable box and the zone scrolls for the rest. */}
          <section className="flex-1 min-h-[14rem] rounded-lg border border-subtle bg-card p-3 flex flex-col overflow-hidden">
            <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
              Move Stack
              {moves.length > 0 && (
                <span className="font-normal normal-case tracking-normal">
                  {' '}— drag to reorder passes
                </span>
              )}
            </div>
            <div className="flex-1 overflow-y-auto">
              {moves.length === 0 ? (
                <div className="text-sm text-muted-foreground py-4 text-center">
                  Anchor a cut on the strip, choose a destination, add the move.
                </div>
              ) : (
                <SortableMoveList
                  moves={moves}
                  trackName={trackName}
                  onReorder={reorderMove}
                  onRemove={removeMove}
                />
              )}
            </div>
          </section>

          <section className="shrink-0 rounded-lg border border-subtle bg-card p-3 flex flex-col gap-2.5">
            <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Crewing
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wide text-muted-foreground mb-1">
                Locomotive
              </label>
              <select
                value={locomotiveId}
                onChange={(e) => setLocomotiveId(e.target.value)}
                className={selectCls}
              >
                <option value="">Select…</option>
                {locomotives.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.reportingMark ?? l.id}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wide text-muted-foreground mb-1">
                Loco on track (optional)
              </label>
              <select
                value={locomotiveTrackId}
                onChange={(e) => setLocomotiveTrackId(e.target.value)}
                className={selectCls}
              >
                <option value="">Auto (snap/registry)</option>
                {tracks.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wide text-muted-foreground mb-1">
                Crew
              </label>
              <select
                value={crewId}
                onChange={(e) => setCrewId(e.target.value)}
                className={selectCls}
              >
                <option value="">Select…</option>
                {crews.map((c) => (
                  <option key={c.crewId} value={c.crewId}>
                    {c.crewName ?? c.crewId}
                  </option>
                ))}
              </select>
            </div>
          </section>
        </div>
      </div>
      {printModel && (
        <SwitchListPrintSheet
          viewModel={printModel}
          active
          onDone={() => setPrintModel(null)}
        />
      )}
      {animateOpen && preview && activeFacilityId && (
        <SwitchListPreviewMapModal
          switchList={preview}
          facilityId={activeFacilityId}
          locomotiveId={locomotiveId}
          onClose={() => setAnimateOpen(false)}
        />
      )}
    </ScreenShell>
  )
}
